import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getAdminPaymentsOverview } from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

function StatCard({ label, value, icon, accent, onClick }) {
  const color = accent || PRIMARY;
  return (
    <div style={{ ...S.card, cursor: onClick ? "pointer" : "default", borderTop: "3px solid " + color }} onClick={onClick}>
      <div style={{ width: 46, height: 46, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <i className={icon} style={{ fontSize: 20, color }} />
      </div>
      <div>
        <div style={S.cardValue}>{value ?? "—"}</div>
        <div style={S.cardLabel}>{label}</div>
      </div>
    </div>
  );
}

export default function PaymentsDashboard() {
  const dispatch = useDispatch();
  const history = useHistory();
  const data = useSelector((s) => s.profile.adminPaymentsOverview);

  useEffect(() => { dispatch(getAdminPaymentsOverview()); }, [dispatch]);

  const fmt = (n) => n?.toLocaleString("en-NG") ?? "—";
  const fmtMoney = (n) => n != null ? `₦${Number(n).toLocaleString("en-NG")}` : "—";

  if (!data) {
    return (
      <div style={S.loading}>
        <div style={S.spinner} />
        <p style={{ color: "#888", marginTop: 16, fontSize: 14 }}>Loading…</p>
      </div>
    );
  }

  return (
    <div style={S.root}>
      <div style={S.pageHeader}>
        <div>
          <h2 style={S.heading}>Payments & Finance</h2>
          <p style={S.headingSub}>Paystack overview and platform earnings</p>
        </div>
      </div>

      <div style={S.sectionLabel}>
        <i className="bi bi-credit-card" style={{ color: PRIMARY, marginRight: 8 }} />Paystack Summary
      </div>
      <div style={S.cardGrid}>
        <StatCard label="Total Transactions" value={fmt(data.totalTransactions)} icon="bi bi-list-ul" accent={PRIMARY} onClick={() => history.push("/admin/payments/transactions")} />
        <StatCard label="Total Settlements" value={fmt(data.totalSettlements)} icon="bi bi-bank" accent="#4fb0c6" onClick={() => history.push("/admin/payments/settlements")} />
        <StatCard label="Open Disputes" value={fmt(data.openDisputes)} icon="bi bi-shield-exclamation" accent="#e63946" onClick={() => history.push("/admin/payments/disputes")} />
        <StatCard label="Total Refunds" value={fmt(data.totalRefunds)} icon="bi bi-arrow-counterclockwise" accent="#f4a261" onClick={() => history.push("/admin/payments/refunds")} />
      </div>

      <div style={S.sectionLabel}>
        <i className="bi bi-wallet2" style={{ color: PRIMARY, marginRight: 8 }} />Wallets
      </div>
      <div style={S.cardGrid}>
        <StatCard label="Total DVA Wallets" value={fmt(data.totalWallets)} icon="bi bi-wallet2" accent="#9b5de5" onClick={() => history.push("/admin/wallets")} />
        <StatCard label="Active Wallets" value={fmt(data.activeWallets)} icon="bi bi-check-circle-fill" accent="#2ec4b6" />
        <StatCard label="TX Fee Earnings" value={fmtMoney(data.txFeeEarnings)} icon="bi bi-graph-up-arrow" accent={PRIMARY} onClick={() => history.push("/admin/earnings")} />
      </div>

      <div style={S.sectionLabel}>
        <i className="bi bi-lightning-charge-fill" style={{ color: PRIMARY, marginRight: 8 }} />Quick Actions
      </div>
      <div style={S.quickGrid}>
        {[
          { label: "All Transactions", path: "/admin/payments/transactions", icon: "bi bi-list-ul", color: PRIMARY },
          { label: "Settlements", path: "/admin/payments/settlements", icon: "bi bi-bank", color: "#4fb0c6" },
          { label: "Disputes", path: "/admin/payments/disputes", icon: "bi bi-shield-exclamation", color: "#e63946" },
          { label: "Refunds", path: "/admin/payments/refunds", icon: "bi bi-arrow-counterclockwise", color: "#f4a261" },
          { label: "Subscription Plans", path: "/admin/subscriptions", icon: "bi bi-card-list", color: "#9b5de5" },
          { label: "Merchant Subscriptions", path: "/admin/subscriptions/merchants", icon: "bi bi-people", color: "#2ec4b6" },
          { label: "Platform Earnings", path: "/admin/earnings", icon: "bi bi-graph-up-arrow", color: PRIMARY },
        ].map((l) => (
          <button key={l.path} style={S.quickBtn} onClick={() => history.push(l.path)}>
            <i className={l.icon} style={{ fontSize: 18, color: l.color, marginBottom: 6 }} />
            <span style={{ fontSize: 12, color: "#555", fontWeight: 500 }}>{l.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 28 },
  heading: { fontSize: 24, fontWeight: 800, color: BG, margin: 0 },
  headingSub: { fontSize: 13, color: "#888", marginTop: 4 },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 14, marginTop: 8, display: "flex", alignItems: "center" },
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 16, marginBottom: 32 },
  card: { background: "#fff", borderRadius: 10, padding: "18px 20px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", display: "flex", alignItems: "flex-start", gap: 14 },
  cardValue: { fontSize: 22, fontWeight: 800, color: BG, lineHeight: 1.2 },
  cardLabel: { fontSize: 12, color: "#888", marginTop: 3 },
  quickGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 12 },
  quickBtn: { background: "#fff", border: "1.5px solid #f0f0f0", borderRadius: 10, padding: "16px 12px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.04)", transition: "box-shadow 0.15s" },
  loading: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 360 },
  spinner: { width: 36, height: 36, borderRadius: "50%", border: "3px solid " + PRIMARY, borderTopColor: "transparent" },
};
